import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  useMediaQuery,
  useTheme,
  SvgIcon,
  IconButton,
  Tooltip,
  tooltipClasses,
} from "@mui/material";
import CarouselComponent from "./Carousel";
import { Planner } from "../preneed-planners.types";
import "./PlannerCard.scss";
import ProviderCardContactModal from "../../../common/components/provider-card-contactus-modal/ProviderCardContactModal";
import { useNavigate } from "react-router-dom";
import store from "../../../store";
import { ReactComponent as NoImgIcon } from "../../../assets/icons/no-img-icon.svg";
import { togglePlannerFavorite } from "../../../api/preneed-planners.api";
import LikeIcon from "../../../assets/icons/like-icon.svg";
import LikedIcon from "../../../assets/icons/liked-icon.svg";
import LikeIconSmall from "../../../assets/icons/like-icon-small.svg";
import LikedIconSmall from "../../../assets/icons/liked-icon-small.svg";
import { StarRounded } from "@mui/icons-material";
import { truncateWords } from "../../../utils/utils";

interface PlannerCardProps {
  planner: Planner;
  view: "grid" | "list";
  onRequestQuote: (planner: Planner) => void;
}

const PlannerCard: React.FC<PlannerCardProps> = ({
  planner,
  view,
  onRequestQuote,
}) => {
  const {
    id,
    businessName,
    description,
    avgRating,
    startingPrice,
    isFavorite,
    fileInfo,
    address,
    contactInfo,
    email,
    phoneNumber,
  } = planner;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [openModal, setOpenModal] = useState(false);

  const [isPlannerFavorite, setIsPlannerFavorite] =
    useState<boolean>(isFavorite);
  const isLoggedIn = store.getState()?.session?.isLoggedIn || false;

  const handleOpen = () => setOpenModal(true);
  const handleClose = () => setOpenModal(false);
  const navigate = useNavigate();

  const handleViewDetails = () => {
    navigate(`${id}`);
  };

  const images =
    fileInfo?.filter(
      (item: any) =>
        (item.mediaType === "image" || item.mediaType === "video") && item.url,
    ) || [];

  const { city, state } = address;

  const handleToggleFavorite = async () => {
    if (!isLoggedIn) return;

    try {
      const response = await togglePlannerFavorite(id);

      if (response.data?.success) {
        setIsPlannerFavorite(response?.data?.data.isFavorite);
      }
    } catch (error) {
      console.error("Error toggling favorite:", error);
    }
  };

  if (isMobile && view === "list") {
    return (
      <Box className="planner-card mobile-list-view">
        <ProviderCardContactModal
          open={openModal}
          onClose={handleClose}
          title={businessName}
          address={address}
          latitude={Number(contactInfo.latitude)}
          longitude={Number(contactInfo.longitude)}
          phoneNumber={phoneNumber}
          email={email}
          contactInfo={contactInfo}
          isPlanner={true}
        />
        <Box className="planner-card__image">
          {images.length === 0 ? (
            <Box className="planner-card__no-image">
              <SvgIcon
                component={NoImgIcon}
                style={{ height: "48px", width: "48px" }}
                inheritViewBox
              />
              <Typography sx={{ fontSize: "10px" }}>No Image</Typography>
            </Box>
          ) : (
            <CarouselComponent
              images={images || []}
              avgRating={avgRating ?? 0}
              view={view}
              isFavorite={isPlannerFavorite}
              onFavoriteToggle={handleToggleFavorite}
              showFavoriteIcon={false}
            />
          )}
        </Box>
        <Box className="center-right-part">
          <Box className="card-info">
            <Typography variant="h6" className="card-info-title">
              {businessName}
            </Typography>
            <Typography variant="body2" className="card-info-location">
              {city && state ? `${city}, ${state}` : "-"}
            </Typography>
            <Typography variant="body2" className="description">
              {description}
            </Typography>
            <Box className="tags">
              {Array.from(new Set(planner.serviceCategories || []))
                .slice(0, 2)
                .map((cat, idx) => (
                  <span key={idx} className="tag">
                    {cat}
                  </span>
                ))}
              {(planner.serviceCategories?.length || 0) > 2 && (
                <span className="tag">
                  +{Array.from(new Set(planner.serviceCategories)).length - 2}
                </span>
              )}
            </Box>
          </Box>
          <Box className="card-actions mobile-list-actions">
            <Typography
              className="price-title"
              sx={{
                fontSize: isMobile ? "12px !important" : "",
              }}
            >
              Starting Price
            </Typography>
            <Typography
              className="price"
              sx={{ fontSize: "14px", fontWeight: 600 }}
            >
              ${startingPrice ?? 0}.00
            </Typography>
            <Box
              className="action-buttons"
              sx={{ display: "flex", gap: 1, mt: 1 }}
            >
              <Button
                fullWidth
                size="large"
                className="contact-info"
                onClick={handleOpen}
                sx={{ fontSize: "12px", width: "82px" }}
              >
                Contact Info
              </Button>
              <Button
                fullWidth
                size="large"
                className="view-details"
                sx={{ fontSize: "14px" }}
                onClick={handleViewDetails}
              >
                View Details
              </Button>
            </Box>
            <Button
              fullWidth
              className="planner-card__quote-btn"
              onClick={() => onRequestQuote(planner)}
              sx={{ mt: 1, textTransform: "none" }}
            >
              Request Quote
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={`planner-card planner-card--${view}`}>
      <ProviderCardContactModal
        open={openModal}
        onClose={handleClose}
        title={businessName}
        address={address}
        latitude={Number(contactInfo.latitude)}
        longitude={Number(contactInfo.longitude)}
        phoneNumber={phoneNumber}
        email={email}
        contactInfo={contactInfo}
        isPlanner={true}
      />

      <Box className="planner-card__image">
        {images.length === 0 ? (
          <Box className="planner-card__no-image">
            <SvgIcon
              component={NoImgIcon}
              style={{
                height: "48px",
                width: "48px",
              }}
              inheritViewBox
            />
            <Typography>No Image Available</Typography>
          </Box>
        ) : (
          <CarouselComponent
            images={images}
            avgRating={avgRating ?? 0}
            isFavorite={isPlannerFavorite}
            onFavoriteToggle={handleToggleFavorite}
            view={view}
            showFavoriteIcon={view === "grid"}
          />
        )}
      </Box>

      {/* Info Section */}
      <Box className="planner-card__info">
        {view === "grid" ? (
          <Box className="planner-card__header planner-card__header--grid">
            <Box className="planner-card__title-rating">
              <Typography
                variant="h6"
                className="planner-card__title"
                onClick={handleViewDetails}
              >
                {businessName}
              </Typography>
              <Box className="planner-card__avg-rating">
                {avgRating && <StarRounded fontSize="small" sx={{ color: "#e9ae10" }} />}
                <Typography variant="body2">
                  {avgRating !== null && avgRating !== undefined
                    ? avgRating.toFixed(1)
                    : ""}
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" className="planner-card__location">
              {city && state ? `${city}, ${state}` : "-"}
            </Typography>
          </Box>
        ) : (
          <Box className="planner-card__header">
            <Typography
              variant="h6"
              className="planner-card__title"
              onClick={handleViewDetails}
            >
              {businessName}
            </Typography>
            <Typography variant="body2" className="planner-card__location">
              {city && state ? `${city}, ${state}` : "-"}
            </Typography>
          </Box>
        )}

        <Tooltip title={description} arrow slotProps={{
          popper: {
            sx: {
              [`&.${tooltipClasses.popper}[data-popper-placement*="bottom"] .${tooltipClasses.tooltip}`]:
              {
                marginTop: '-12px',
                fontSize: "16px"
              },
            },
          },
        }}>
          <Typography className="description">{truncateWords(description ?? "", 10)}</Typography>
        </Tooltip>

        <Box className="tags">
          {Array.from(new Set(planner.serviceCategories || []))
            .slice(0, view === "grid" ? 2 : 3)
            .map((cat, idx) => (
              <span key={idx} className="tag">
                {cat}
              </span>
            ))}
          {Array.from(new Set(planner.serviceCategories || [])).length > (view === "grid" ? 2 : 3) && (
            <span className="tag">
              +{Array.from(new Set(planner.serviceCategories || [])).length - (view === "grid" ? 2 : 3)}
            </span>
          )}
        </Box>
      </Box>

      {/* Actions Section */}
      <Box className="planner-card__actions">
        <Box className="planner-card__price">
          <Typography className="planner-card__starting-price">
            Starting Price
          </Typography>
          <Typography className="planner-card__price-value">
            ${startingPrice ?? 0}.00
          </Typography>
        </Box>

        <Box className="planner-card__buttons">
          <Button
            variant="outlined"
            size="small"
            className="planner-card__contact-info"
            onClick={handleOpen}
          >
            Contact Info
          </Button>
          <Button
            size="small"
            className="planner-card__view-details"
            onClick={handleViewDetails}
          >
            View Details
          </Button>
          {/* {isLoggedIn && view === "list" && (
            <Button
              size="small"
              className="wishlist-icon"
              sx={{
                minWidth: "38px",
                borderRadius: "8px",
                border: "1px solid #15585D",
                padding: "0.3rem",
              }}
              onClick={handleToggleFavorite}
            >
              {isPlannerFavorite ? (
                <img src={LikedIcon} alt="wishlist icon" />
              ) : (
                <img src={LikeIcon} alt="wishlist icon" />
              )}
            </Button>
          )} */}
        </Box>

        <Button
          className="planner-card__quote-btn"
          onClick={() => onRequestQuote(planner)}
        >
          Request Quote
        </Button>
      </Box>
    </Box>
  );
};

export default PlannerCard;
